import {
  CircleCheckBig,
  CircleDot,
  Logs,
  NotepadText,
  Timer,
} from "lucide-react";
import { countTaskLogLines } from "@/components/tasks/details/TaskLogEditor/TaskLogEditor";
import { formatDateTimeDisplay, formatDurationShort } from "@/lib/time";

type Props = {
  status?: string | null;
  startedAt?: Date | string | null;
  completedAt?: Date | string | null;
  notes?: string | null;
  log?: string | null;
  color: string;
  className?: string;
  showStarted?: boolean;
  showIcons?: boolean;
  notesDisplay?: "icon" | "text";
};

function timeLabel(value: Date | string | null | undefined) {
  if (!value) return "";
  return formatDateTimeDisplay(new Date(value));
}

function durationLabel(startedAt: Date | string | null | undefined, completedAt: Date | string | null | undefined) {
  if (!startedAt || !completedAt) return "";
  const start = new Date(startedAt).getTime();
  const end = new Date(completedAt).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return "";
  return formatDurationShort(Math.round((end - start) / 60000));
}

export function hasTaskResults({
  status,
  startedAt,
  completedAt,
  notes,
  log,
}: Pick<Props, "status" | "startedAt" | "completedAt" | "notes" | "log">) {
  return status === "skipped" || Boolean(startedAt) || Boolean(completedAt) || Boolean(notes) || countTaskLogLines(log) > 0;
}

export default function TaskResultsSummary({
  status,
  startedAt,
  completedAt,
  notes,
  log,
  color,
  className = "",
  showStarted = true,
  showIcons = true,
  notesDisplay = "icon",
}: Props) {
  const started = timeLabel(startedAt);
  const ended = timeLabel(completedAt);
  const duration = durationLabel(startedAt, completedAt);
  const logCount = countTaskLogLines(log);
  const hasNotes = Boolean(notes);
  const isSkipped = status === "skipped";

  if (!isSkipped && !started && !ended && !duration && !hasNotes && logCount === 0) return null;

  return (
    <div
      className={`ui-task-results-summary space-y-0 text-xs tabular-nums ${className}`}
      style={{ color, opacity: 0.78 }}
    >
      <div className="ui-task-results-summary__meta flex flex-wrap gap-x-2 gap-y-1 pt-[0.5px]">
        {isSkipped && <span className="ui-result-skipped">Skipped</span>}
        {showStarted && started && (
          <span className="ui-icon-dot inline-flex items-center gap-1" title="Started">
            {showIcons && <CircleDot className="h-3 w-3" />}
            {started}
          </span>
        )}

        {(started && ended) && (
          <span className="ui-between">-</span>

        )}


        {ended && (
          <span className="ui-icon-check inline-flex items-center gap-1" title="Ended">
            {showIcons && <CircleCheckBig className="h-3 w-3" />}
            {ended}
          </span>
        )}
        {duration && (
          <span className="ui-icon-timer inline-flex items-center gap-1" title="Duration">
            {showIcons && <Timer className="h-3 w-3" />}
            {duration}
          </span>
        )}
        {/* {logCount > 0 && ( */}
        {false && (
          <span className="ui-icon-logs inline-flex items-center gap-1 hidden" title={logCount === 1 ? "Log" : "Logs"}>
            <Logs className="h-3 w-3" />
            {logCount}
          </span>
        )}
        {showIcons && hasNotes && notesDisplay === "icon" && (
          <span className="ui-icon-notes inline-flex items-center gap-1" title="Notes">
            <NotepadText className="h-3 w-3" />
          </span>
        )}
      </div>
      {hasNotes && notesDisplay === "text" && (
        <div className="ui-task-results_notes w-full whitespace-pre-wrap leading-relaxed">
          <hr className="mb-1.5" />
          <span className="ui-icon-notes inline-flex items-center gap-1 align-[-0.125em]">
            <NotepadText className="h-3 w-3" />
          </span>{" "}
          {notes}
        </div>
      )}
    </div>
  );
}
